import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  CreationClassExtended,
  ItemViewModel,
  SubmitCreation,
  SubmitCreationResponse,
  SubmitCreationRightsHolder,
} from "../../../../generated/data-contracts";
import { CreationType } from "../../../../models";
import { ErrorUtils, api } from "../../../../utils";
import { AvisForm, AvisLoader } from "../../../shared/components";
import { AvisConfirmModalShort } from "../../../shared/components/modal";
import { AvisContext } from "../../../shared/context/AvisContext";
import { ScrollToFieldError } from "../../../shared/formik/ScrollToFieldError";
import { CreationsNewForm } from "./CreationsNewForm";
import { validationSchema } from "./validation";
import { IRowState } from "../../../shared/components/editableList";

interface ICreationsNewPageProps {
  defaultCreationType: CreationType;
  creationTypeReadOnly?: boolean;
  ///External event for unidentified creation recognition process
  onCreationCreated?: (createdItem: ItemViewModel) => void;
  hideButtons?: boolean;
}
export function CreationsNewPage(props: Readonly<ICreationsNewPageProps>) {
  const {
    onCreationCreated,
    hideButtons,
    defaultCreationType,
    creationTypeReadOnly,
  } = props;

  const { res, ctxRightsHolder, ctxPortalSettings } = useContext(AvisContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [classifiers, setClassifiers] = useState<CreationClassExtended[]>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCreationClassCode, setSelectedCreationClassCode] =
    useState<CreationType>(defaultCreationType);

  useEffect(() => {
    setIsLoading(true);

    api
      .classifiersGetCreationClassesWithGenresAndRoles()
      .then((res) => setClassifiers(res.data))
      .catch((err) => ErrorUtils.handleAxiosErrorResponse(err, res))
      .finally(() => setIsLoading(false));
  }, [res]);

  const handleSubmit = (
    values: SubmitCreation,
    formikHelpers: FormikHelpers<SubmitCreation>,
  ) => {
    if (!ctxRightsHolder) return;
    setIsSubmitting(true);

    api
      .rightsHoldersAddCreation(ctxRightsHolder?.rightsHolderId, values)
      .then((response) => {
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          res("authors_CreationNew_label_Added"),
        );
        setIsSubmitted(true);
        const responseData = response.data as SubmitCreationResponse;

        onCreationCreated?.({
          id: responseData.creationId,
          name: values.creationName,
        });
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      });
  };

  const resetForm = useCallback(() => {
    setIsSubmitted(false);
    setSelectedCreationClassCode(defaultCreationType);
    setTimeout(() => {
      const ddlCreationClass = document.getElementsByClassName(
        "creation-class-dropdown",
      )[0];
      if (ddlCreationClass)
        ddlCreationClass.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
    }, 100);
  }, [defaultCreationType]);

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitCreation>) => {
      const onSelectedCreationClassItemChange = (
        selectedItemId: number | undefined,
      ) => {
        if (!selectedItemId) return;
        const foundOption = classifiers?.find((i) => i.id === selectedItemId);
        const valueToSet = foundOption
          ? (foundOption.code as CreationType)
          : defaultCreationType;

        setSelectedCreationClassCode(valueToSet);
      };

      const genres = classifiers
        ? classifiers.filter((cc) => cc.code === selectedCreationClassCode)[0]
            .creationGenres
        : [];
      const roles = classifiers
        ? classifiers.filter((cc) => cc.code === selectedCreationClassCode)[0]
            .creationRoles
        : [];

      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("authors_CreationNew_ConfirmNew")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res("title_Confirm")}
            disableControls={isSubmitting}
          />
          <CreationsNewForm
            creationType={selectedCreationClassCode}
            creationTypeReadOnly={creationTypeReadOnly}
            bag={formikProps}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            isSubmitted={isSubmitted}
            creationClasses={classifiers as ItemViewModel[]}
            genres={genres}
            roles={roles}
            hideButtons={hideButtons}
            showModal={() => setIsModalOpen(true)}
            allowedFileFormats={ctxPortalSettings?.uploadableDocumentTypes}
            resetForm={resetForm}
            onSelectedCreationClassItemChange={
              onSelectedCreationClassItemChange
            }
          />
          <ScrollToFieldError />
        </>
      );
    },
    [
      classifiers,
      isModalOpen,
      res,
      isSubmitting,
      selectedCreationClassCode,
      creationTypeReadOnly,
      isLoading,
      isSubmitted,
      hideButtons,
      ctxPortalSettings?.uploadableDocumentTypes,
      resetForm,
      defaultCreationType,
    ],
  );

  if (isLoading || !classifiers) return <AvisLoader />;

  const defaultCreationTypeItem: CreationClassExtended | undefined =
    classifiers?.find((el) => el.code === defaultCreationType);

  if (!defaultCreationTypeItem?.id) return <AvisLoader />;

  return (
    <AvisForm
      title={res("authors_CreationNew_label_PageTitle")}
      className="creation-new-page"
    >
      <Formik<SubmitCreation>
        initialValues={{
          creationClassId: defaultCreationTypeItem.id,
          creationName: "",
          notes: "",
          timings: ctxPortalSettings?.defaultCreationTiming,
          creationBands: [],
          creationFiles: [],
          rightsHolders: [
            {
              additionalInformation: `${ctxRightsHolder?.firstName} ${ctxRightsHolder?.lastName}`,
              creationRoleId: 0,
              isCurrentRightsHolder: true,
              rowState: "Add",
            } as IRowState<SubmitCreationRightsHolder>,
          ],
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(
          selectedCreationClassCode,
          ctxPortalSettings?.uploadableDocumentTypes,
          ctxPortalSettings?.unsupportedFilenameSymbols ?? [],
          ctxPortalSettings?.maxFileUploadSize ?? 0,
          res,
        )}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderForm}
      </Formik>
      {isSubmitting && <AvisLoader />}
    </AvisForm>
  );
}
